import { useState } from "react";
import { CheckCircle2, ShieldCheck } from "lucide-react";
import { PlaidIDVButton } from "@/components/PlaidIDVButton";
import { applicantApi } from "@/lib/api";

/**
 * BF-C1 / ADR-012 R3 — applicant-side identity verification.
 *
 * Rendered INSIDE the email step for the ADR-012 org-flag path, never as its
 * own step there: the `steps` array indices in ApplicantFlow.tsx are
 * load-bearing (dozens of `current === N` conditionals key off them), so
 * inserting an early step would shift every one of them.
 *
 * ## Two callers, two regimes — see `requiredByFlow`
 *
 * ADR-014 added a second caller: a workflow definition can name `plaid_idv` as
 * an ordered, REQUIRED step, which ApplicantFlow renders on its own screen
 * (appended index 6, so no index moved). That regime is not the one the rules
 * below were written for, and `requiredByFlow` is what separates them. Nothing
 * about the org-flag path changed.
 *
 * The rules this component exists to hold, all from ADR-012 R3, and all
 * applying to the `requiredByFlow={false}` default:
 *
 *   - **Continue is never gated.** Nothing here blocks submission, and no
 *     caller should add a gate. Declining is a choice, not an admission
 *     (company constitution, Data ethics §5).
 *   - **The decline is always available**, as a quiet underlined text link
 *     that is visually subordinate to the primary action — the same hierarchy
 *     the pay-stub upload fallback uses, and for the same reason: weight is
 *     what keeps people on the stronger path. Do not promote it to a button.
 *   - **One retry**, then the decline becomes the prominent path (`maxRetries`
 *     = 1). The voucher flow keeps its 2 via the prop default.
 *   - **What it buys is stated, because that's the motivation in place of a
 *     block** — a completed IDV means one fewer vouch (ADR-012 R2).
 *
 * Copy rules: never the word "optional" on an IDV screen (voucher-flow
 * precedent), and none of the banned vocabulary — no "fail", "risk", "fraud",
 * "pass". A declined or failed IDV is a workflow fact, never an accusation.
 */

export interface ApplicantIDVCardProps {
  /** Applicant session token. Absent in demo mode, where this renders nothing. */
  token: string | undefined;
  /**
   * IDV has already passed on the backend.
   *
   * ⚠️ Pass `isIdvSatisfied(session)`, never `session.applicant_idv_verified`
   * on its own — both call sites do, and `idvCardPredicate.test.ts` enforces
   * it. Two independent facts can each establish this and a caller reading
   * only one is wrong for some org: the attestation lookup behind
   * `applicant_idv_verified` runs only when the org sets
   * `applicant_idv_required`, and `steps_completed` gains `plaid_idv` on a
   * passing capture through a write that sits outside the capture transaction.
   * Reading the flag alone is what rendered an enabled "Verify your identity"
   * button over an already-passed IDV (#186).
   */
  verified: boolean;
  /** session.applicant_idv_skipped — already declined on the backend. */
  skipped: boolean;
  /** How many vouch requests the applicant would otherwise need (ADR-012 R2). */
  vouchesWithoutIdv?: number | null;
  /**
   * The step is REQUIRED by this session's workflow definition (ADR-014), not
   * merely asked for by the org flag (ADR-012 R3). Two things change:
   *
   *  - **No decline link.** `POST /verify/plaid-idv/skip` sets
   *    `voucher_idv_skipped` and does NOT write `plaid_idv` into
   *    `steps_completed`, so a skip cannot satisfy a required step
   *    (`isStepSatisfied` matches on the step type or its `satisfied_by`
   *    alternatives, and `plaid_idv` is seeded with neither). Offering a
   *    decline here would be offering a dead end.
   *  - **No vouch-credit promise.** The ADR-012 R2 credit is gated on the org
   *    flag `applicant_idv_required`, which the IDW demo org does not set — so
   *    "one fewer vouch request" would be an overclaim on this path.
   *
   * 👤 Worth revisiting after the conference: a required step with no decline
   * has no escape hatch for an applicant whose ID won't scan. Correct for a
   * mandatory-posture flow, and correct for a booth demo, but it is a product
   * decision rather than a technical one.
   */
  requiredByFlow?: boolean;
  /** Refetch the session so the parent picks up the new state. */
  onChanged?: () => void;
}

export function ApplicantIDVCard({
  token,
  verified,
  skipped,
  vouchesWithoutIdv,
  requiredByFlow = false,
  onChanged,
}: ApplicantIDVCardProps) {
  // Local echo of the backend flags so the card responds immediately; the
  // parent's refetch is what makes it durable.
  const [localVerified, setLocalVerified] = useState(false);
  const [localSkipped, setLocalSkipped] = useState(false);
  // "Changed my mind" has to be able to override the PERSISTED skip, not just
  // the local one — on any reload the decline arrives via the `skipped` prop,
  // which is exactly when someone is most likely to reconsider. Without this
  // the reconsider affordance silently does nothing in the only case that
  // matters. The backend flag is left alone: /skip has no inverse, and the
  // decline stays on the record for the operator (BF-B2). Completing IDV is
  // what supersedes it, which is why `verified` outranks `declined` there.
  const [reconsidered, setReconsidered] = useState(false);
  const [declining, setDeclining] = useState(false);

  const isVerified = verified || localVerified;
  // A required step has no decline, so it also has no declined state to render.
  // A pre-existing `applicant_idv_skipped` (set on some earlier, org-flag
  // session) must not park a required step on "you chose to skip".
  const isSkipped = !requiredByFlow && (skipped || localSkipped) && !reconsidered;

  const decline = async () => {
    if (!token || declining) return;
    setDeclining(true);
    try {
      await applicantApi.plaidIdvSkip(token);
      setLocalSkipped(true);
      setReconsidered(false);
      onChanged?.();
    } finally {
      setDeclining(false);
    }
  };

  if (!token) return null;

  if (isVerified) {
    return (
      <div
        data-testid="applicant-idv-verified"
        className="rounded-lg border border-trust-green/30 bg-trust-green/5 p-4 flex items-start gap-3"
      >
        <CheckCircle2 className="w-5 h-5 text-trust-green shrink-0 mt-0.5" />
        <div className="space-y-1">
          <p className="text-sm font-medium">Identity verified</p>
          <p className="text-xs text-muted-foreground leading-relaxed">
            {requiredByFlow
              ? "Thanks — that's done. You can carry on with the rest of your application."
              : "Thanks — that's done. You'll need one fewer vouch request as a result."}
          </p>
        </div>
      </div>
    );
  }

  // Declined — recorded, and deliberately undramatic. The operator sees it
  // (BF-B2), but nothing about the applicant's submission changes, so the copy
  // must not imply a consequence that doesn't exist. Re-offering the option is
  // intentional: a decline is not terminal, and someone who changes their mind
  // should be able to act on it.
  const declinedNote = isSkipped ? (
    <div
      data-testid="applicant-idv-declined"
      className="rounded-lg border border-border bg-muted/40 p-4 space-y-2"
    >
      <p className="text-sm font-medium">You chose to skip identity verification</p>
      <p className="text-xs text-muted-foreground leading-relaxed">
        That's recorded, and you can carry on with your application. You'll be asked
        for one more vouch request instead.
      </p>
      <button
        type="button"
        onClick={() => setReconsidered(true)}
        className="text-xs underline text-muted-foreground hover:text-foreground"
      >
        Change my mind and verify now
      </button>
    </div>
  ) : null;

  if (declinedNote) return declinedNote;

  const vouchLine = requiredByFlow
    ? "It takes about a minute."
    : typeof vouchesWithoutIdv === "number" && vouchesWithoutIdv > 1
      ? `Verifying now means ${vouchesWithoutIdv - 1} vouch request${
          vouchesWithoutIdv - 1 === 1 ? "" : "s"
        } instead of ${vouchesWithoutIdv}.`
    : "Verifying now means one fewer vouch request to send.";

  // The decline link. Subordinate by weight — underlined text, muted, never a
  // button — but always present, per R3.3.
  const declineLink = requiredByFlow ? null : (
    <button
      type="button"
      onClick={decline}
      disabled={declining}
      data-testid="applicant-idv-decline"
      className="text-xs underline text-muted-foreground hover:text-foreground disabled:opacity-60"
    >
      {declining ? "One moment…" : "I'd rather not verify my identity"}
    </button>
  );

  return (
    <div
      data-testid="applicant-idv-card"
      className="rounded-lg border border-border bg-card p-4 space-y-3"
    >
      <div className="flex items-start gap-3">
        <ShieldCheck className="w-5 h-5 text-primary shrink-0 mt-0.5" />
        <div className="space-y-1">
          {/* On a definition-driven flow this card IS the screen, and the screen
              already has "Verify your ID" as its header — a second heading here
              stacked three near-identical phrases (header, this, and the Plaid
              button) on the demo's signature screen. On the ADR-012 path the
              card sits inside the email step and still needs its own title. */}
          {!requiredByFlow && <p className="text-sm font-medium">Verify your identity</p>}
          <p className="text-xs text-muted-foreground leading-relaxed">
            A quick check with a government ID confirms you're you. {vouchLine}
          </p>
        </div>
      </div>

      <PlaidIDVButton
        token={token}
        verified={isVerified}
        // Never pass the persisted skip here: this card renders its own
        // declined state above, and PlaidIDVButton's `skipped` branch shows
        // the voucher's "flagged for operator review" copy.
        skipped={false}
        // ADR-012 R3.3 — one retry for applicants; the voucher flow keeps 2.
        maxRetries={1}
        onSuccess={() => {
          setLocalVerified(true);
          onChanged?.();
        }}
        // Exhausting the retry does NOT auto-decline. Recording a decline the
        // applicant didn't choose would put a false statement in front of the
        // operator, and R3 makes declining an explicit choice. The decline link
        // below simply becomes the prominent path.
        onMaxRetriesReached={() => undefined}
        exhaustedToast={{
          title: "No problem — you can continue",
          description:
            "Identity verification didn't complete. You can carry on with your application.",
        }}
        exhaustedSlot={
          <div
            data-testid="applicant-idv-exhausted"
            className="rounded-lg border border-border bg-muted/40 p-3 space-y-2"
          >
            <p className="text-xs text-muted-foreground leading-relaxed">
              {requiredByFlow
                ? "We couldn't complete the identity check this time. Reload this page to try again."
                : "We couldn't complete the identity check this time. You can carry on with your application — you'll be asked for one more vouch request instead."}
            </p>
            {declineLink}
          </div>
        }
      />

      {declineLink && <div className="flex justify-center">{declineLink}</div>}
    </div>
  );
}
